import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ElementRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzSelectModule } from 'ng-zorro-antd/select';

@Component({
  selector: 'rapider-select',
  standalone: true,
  imports: [CommonModule, FormsModule, NzSelectModule],
  // CRITICAL FIX: Web Components default to inline. This forces it to fill the parent wrapper,
  // vertically centers the text, and strips internal borders so your Tailwind wrapper div styling shines through.
  host: {
    class:
      'block w-full h-full [&_.ant-select]:!w-full [&_.ant-select]:!h-full [&_.ant-select-selector]:!h-full [&_.ant-select-selector]:!border-none [&_.ant-select-selector]:!bg-transparent [&_.ant-select-selector]:!shadow-none [&_.ant-select-selection-item]:!flex [&_.ant-select-selection-item]:!items-center',
  },
  template: `
    <nz-select
      [ngModel]="value"
      (ngModelChange)="onValueChange($event)"
      [nzId]="id"
      [nzAutoClearSearchValue]="parsedAutoClearSearchValue"
      [nzAllowClear]="parsedAllowClear"
      [nzBackdrop]="parsedBackdrop"
      [nzVariant]="variant"
      [nzOpen]="parsedOpen"
      [nzAutoFocus]="parsedAutoFocus"
      [nzDisabled]="parsedDisabled"
      [nzDropdownClassName]="dropdownClassName"
      [nzDropdownMatchSelectWidth]="parsedDropdownMatchSelectWidth"
      [nzServerSearch]="parsedServerSearch"
      [nzMaxMultipleCount]="parsedMaxMultipleCount"
      [nzMode]="parsedMode"
      [nzNotFoundContent]="notFoundContent"
      [nzPlaceHolder]="placeHolder || placeholder"
      [nzShowArrow]="parsedShowArrow"
      [nzShowSearch]="parsedShowSearch"
      [nzSize]="size"
      [nzStatus]="status"
      [nzTokenSeparators]="parsedTokenSeparators"
      [nzLoading]="parsedLoading"
      [nzMaxTagCount]="parsedMaxTagCount"
      [nzOptionHeightPx]="parsedOptionHeightPx"
      [nzOptionOverflowSize]="parsedOptionOverflowSize"
      [nzSelectOnTab]="parsedSelectOnTab"
      class="w-full text-left"
    >
      <nz-option
        *ngFor="let opt of parsedOptions"
        [nzValue]="opt.value"
        [nzLabel]="opt.label"
        [nzDisabled]="opt.disabled"
        [nzHide]="opt.hide"
      >
      </nz-option>
    </nz-select>
  `,
})
export class RapiderSelectComponent implements OnChanges {
  private readonly el = inject(ElementRef);

  // Core properties
  @Input() options: string = '[]';
  @Input() value: any = '';
  @Input('state-key') stateKey: string = '';

  // Mapped NG-Zorro Inputs (without "nz" prefix)
  @Input() id: string = '';
  @Input() autoClearSearchValue: string = 'true';
  @Input() allowClear: string = 'false';
  @Input() backdrop: string = 'false';
  @Input() variant: 'outlined' | 'borderless' | 'filled' | 'underlined' =
    'outlined';
  @Input() open: string = 'false';
  @Input() autoFocus: string = 'false';
  @Input() disabled: string = 'false';
  @Input() dropdownClassName: string = '';
  @Input() dropdownMatchSelectWidth: string = 'true';
  @Input() serverSearch: string = 'false';
  @Input() maxMultipleCount: string = 'Infinity';
  @Input() mode: 'multiple' | 'tags' | 'default' = 'default';
  @Input() notFoundContent: string = 'Not Found';
  @Input() placeHolder: string = '';
  @Input() placeholder: string = '';
  @Input() showArrow: string = '';
  @Input() showSearch: string = 'false';
  @Input() size: 'large' | 'small' | 'default' = 'default';
  @Input() status: 'error' | 'warning' | '' = '';
  @Input() tokenSeparators: string = '[]';
  @Input() loading: string = 'false';
  @Input() maxTagCount: string = '';
  @Input() optionHeightPx: string = '32';
  @Input() optionOverflowSize: string = '8';
  @Input() selectOnTab: string = 'false';

  public parsedOptions: any[] = [];

  // Coerced strongly-typed values
  public parsedAutoClearSearchValue = true;
  public parsedAllowClear = false;
  public parsedBackdrop = false;
  public parsedOpen = false;
  public parsedAutoFocus = false;
  public parsedDisabled = false;
  public parsedDropdownMatchSelectWidth = true;
  public parsedServerSearch = false;
  public parsedMaxMultipleCount = Infinity;
  public parsedMode: 'multiple' | 'tags' | 'default' = 'default';
  public parsedShowArrow = true;
  public parsedShowSearch = false;
  public parsedTokenSeparators: string[] = [];
  public parsedLoading = false;
  public parsedMaxTagCount: number = Infinity;
  public parsedOptionHeightPx = 32;
  public parsedOptionOverflowSize = 8;
  public parsedSelectOnTab = false;

  private parseBool(val: string, fallback: boolean): boolean {
    if (val === 'true' || val === '') return true;
    if (val === 'false') return false;
    return fallback;
  }

  private parseNum(val: string, fallback: number): number {
    if (!val || val === 'Infinity') return Infinity;
    const n = Number(val);
    return isNaN(n) ? fallback : n;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && this.options) {
      try {
        this.parsedOptions = JSON.parse(this.options);
      } catch (e) {
        console.error('[RapiderSelect] Failed to parse options JSON', e);
        this.parsedOptions = [];
      }
    }

    // Coerce HTML string attributes to booleans and numbers
    this.parsedAutoClearSearchValue = this.parseBool(
      this.autoClearSearchValue,
      true,
    );
    this.parsedAllowClear = this.parseBool(this.allowClear, false);
    this.parsedBackdrop = this.parseBool(this.backdrop, false);
    this.parsedOpen = this.parseBool(this.open, false);
    this.parsedAutoFocus = this.parseBool(this.autoFocus, false);
    this.parsedDisabled = this.parseBool(this.disabled, false);
    this.parsedDropdownMatchSelectWidth = this.parseBool(
      this.dropdownMatchSelectWidth,
      true,
    );
    this.parsedServerSearch = this.parseBool(this.serverSearch, false);
    this.parsedShowSearch = this.parseBool(this.showSearch, false);
    this.parsedLoading = this.parseBool(this.loading, false);
    this.parsedSelectOnTab = this.parseBool(this.selectOnTab, false);

    let arrowFallback = true;
    if (this.mode === 'multiple' || this.mode === 'tags') {
      this.parsedMode = this.mode;
      arrowFallback = false;

      // CRITICAL FIX: Ensure `value` is always an array for multiple/tags mode to prevent nz-select crash
      if (!this.value || this.value === '') {
        this.value = [];
      } else if (typeof this.value === 'string') {
        if (this.value.startsWith('[')) {
          try {
            this.value = JSON.parse(this.value);
          } catch (e) {
            this.value = [];
          }
        } else {
          // Fallback if a flat string was passed to a multiple select
          this.value = [this.value];
        }
      } else if (!Array.isArray(this.value)) {
        this.value = [this.value];
      }
    } else {
      this.parsedMode = 'default';
    }

    this.parsedShowArrow = this.showArrow
      ? this.parseBool(this.showArrow, arrowFallback)
      : arrowFallback;
    this.parsedMaxMultipleCount = this.parseNum(
      this.maxMultipleCount,
      Infinity,
    );
    this.parsedMaxTagCount = this.parseNum(this.maxTagCount, Infinity);
    this.parsedOptionHeightPx = this.parseNum(this.optionHeightPx, 32);
    this.parsedOptionOverflowSize = this.parseNum(this.optionOverflowSize, 8);

    try {
      this.parsedTokenSeparators = JSON.parse(this.tokenSeparators);
    } catch {
      this.parsedTokenSeparators = [];
    }
  }

  public onValueChange(newValue: any): void {
    this.value = newValue;

    // 1. Expose the value on the native HTML element directly.
    // This allows `e.target.value` to work in standard event listeners exactly like a native <select>.
    this.el.nativeElement.value = newValue;

    // 2. Dispatch a standard DOM 'change' event.
    const changeEvent = new Event('change', {
      bubbles: true,
      composed: true, // Crucial: Allows the event to escape the Shadow DOM if you ever use it
    });
    this.el.nativeElement.dispatchEvent(changeEvent);

    // Optional: Dispatch your custom event as well so you don't break existing legacy code
    const legacyEvent = new CustomEvent('state-change', {
      detail: { key: this.stateKey, value: newValue },
      bubbles: true,
      composed: true,
    });
    this.el.nativeElement.dispatchEvent(legacyEvent);
  }
}
